import { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { useAuth } from "../../context/AuthContext";
import {
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  collection,
} from "firebase/firestore";
import { db } from "../../firebase";
import { auth } from "../../firebase";
import {
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from "firebase/auth";
import { requestPermissionAndGetToken } from "../../firebaseMessaging";
import { isNativePlatform, getNativeToken } from "../../utils/nativeNotifications";

// ─── Modal de cambio de contraseña ───────────────────────────────────────────
function ChangePasswordModal({ onClose }) {
  const { currentUser } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword]         = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading]                 = useState(false);
  const [error, setError]                     = useState("");
  const [success, setSuccess]                 = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (newPassword.length < 6) {
      return setError("La nueva contraseña debe tener al menos 6 caracteres.");
    }
    if (newPassword !== confirmPassword) {
      return setError("Las contraseñas nuevas no coinciden.");
    }
    if (currentPassword === newPassword) {
      return setError("La nueva contraseña debe ser diferente a la actual.");
    }

    setLoading(true);
    try {
      // Firebase requiere re-autenticación reciente antes de cambiar contraseña
      const credential = EmailAuthProvider.credential(
        currentUser.email,
        currentPassword
      );
      await reauthenticateWithCredential(auth.currentUser, credential);
      await updatePassword(auth.currentUser, newPassword);
      setSuccess(true);
    } catch (err) {
      switch (err.code) {
        case "auth/wrong-password":
        case "auth/invalid-credential":
          setError("La contraseña actual es incorrecta.");
          break;
        case "auth/weak-password":
          setError("La nueva contraseña es demasiado débil. Usa al menos 6 caracteres.");
          break;
        case "auth/too-many-requests":
          setError("Demasiados intentos fallidos. Espera unos minutos e intenta de nuevo.");
          break;
        case "auth/requires-recent-login":
          setError("Tu sesión expiró. Cierra sesión, vuelve a entrar e intenta de nuevo.");
          break;
        default:
          setError("Ocurrió un error. Intenta de nuevo.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-700 px-5 py-4 flex items-center gap-3">
          <span className="text-2xl">🔐</span>
          <h2 className="text-white font-bold text-lg">Cambiar contraseña</h2>
        </div>

        <div className="px-5 py-5">
          {success ? (
            <div className="text-center space-y-4">
              <span className="text-5xl block">✅</span>
              <p className="font-semibold text-gray-800">
                Contraseña actualizada correctamente
              </p>
              <button
                onClick={onClose}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2.5 rounded-xl transition"
              >
                Cerrar
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Contraseña actual
                </label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Nueva contraseña
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  placeholder="Mínimo 6 caracteres"
                  className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Confirmar nueva contraseña
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  placeholder="Repite la nueva contraseña"
                  className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>

              {error && (
                <p className="text-red-500 text-xs font-medium bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  {error}
                </p>
              )}

              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={loading}
                  className="flex-1 border border-gray-300 text-gray-700 font-semibold py-2.5 rounded-xl hover:bg-gray-100 transition text-sm"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading || !currentPassword || !newPassword || !confirmPassword}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2.5 rounded-xl transition text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? "Cambiando..." : "Cambiar"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Configuración principal ──────────────────────────────────────────────────
export default function Configuracion() {
  const { currentUser } = useAuth();
  const [notificacionesActivadas, setNotificacionesActivadas] = useState(false);
  const [cargando, setCargando]                               = useState(true);
  const [showChangePassword, setShowChangePassword]           = useState(false);

  useEffect(() => {
    const verificarEstado = async () => {
      if (!currentUser) {
        setNotificacionesActivadas(false);
        setCargando(false);
        return;
      }
      try {
        const userDoc = await getDoc(doc(db, "users", currentUser.uid));
        const activo = userDoc.exists()
          ? userDoc.data().notificaciones !== false
          : false;
        setNotificacionesActivadas(activo);
      } catch (error) {
        console.error("Error cargando configuración:", error);
        setNotificacionesActivadas(false);
      } finally {
        setCargando(false);
      }
    };
    verificarEstado();
  }, [currentUser]);

  const activarNotificaciones = async () => {
    try {
      // En app nativa Android usa el plugin de Capacitor; en web usa el service worker
      const token = isNativePlatform()
        ? await getNativeToken()
        : await requestPermissionAndGetToken();

      if (!token) throw new Error("No se obtuvo token FCM");

      await setDoc(doc(db, "fcmTokens", currentUser.uid), { token });
      await updateDoc(doc(db, "users", currentUser.uid), { notificaciones: true });

      const promoProfilesSnap = await getDocs(
        collection(db, "users", currentUser.uid, "promoProfiles")
      );
      for (const promoDoc of promoProfilesSnap.docs) {
        await setDoc(doc(db, "fcmTokens", promoDoc.id), { token });
      }

      setNotificacionesActivadas(true);
    } catch (err) {
      console.error("Error al activar notificaciones:", err.message);
      Swal.fire({ toast: true, position: "top-end", icon: "error", title: "No se pudieron activar las notificaciones.", showConfirmButton: false, timer: 3500 });
    }
  };

  const desactivarNotificaciones = async () => {
    try {
      await deleteDoc(doc(db, "fcmTokens", currentUser.uid));
      await updateDoc(doc(db, "users", currentUser.uid), { notificaciones: false });

      const promoProfilesSnap = await getDocs(
        collection(db, "users", currentUser.uid, "promoProfiles")
      );
      for (const promoDoc of promoProfilesSnap.docs) {
        await deleteDoc(doc(db, "fcmTokens", promoDoc.id));
      }

      setNotificacionesActivadas(false);
    } catch (err) {
      console.error("Error al desactivar notificaciones:", err.message);
      Swal.fire({ toast: true, position: "top-end", icon: "error", title: "No se pudieron desactivar las notificaciones.", showConfirmButton: false, timer: 3500 });
    }
  };

  const manejarToggle = () => {
    if (notificacionesActivadas) desactivarNotificaciones();
    else activarNotificaciones();
  };

  if (cargando) return <p>Cargando configuración...</p>;
  if (!currentUser) return <p>Debes iniciar sesión para configurar las notificaciones.</p>;

  return (
    <div className="mt-4 text-sm text-gray-700">
      <h2 className="text-lg font-semibold mb-4">Configuración</h2>

      {/* Notificaciones */}
      <div className="flex items-center justify-between bg-gray-100 rounded-lg p-4 mb-3">
        <div>
          <p className="font-medium">Notificaciones</p>
          <p className="text-xs text-gray-500">
            {notificacionesActivadas
              ? "Activadas"
              : "Desactivadas o bloqueadas por el navegador"}
          </p>
        </div>
        <button
          onClick={manejarToggle}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            notificacionesActivadas ? "bg-green-500" : "bg-gray-300"
          }`}
          aria-label="Toggle notificaciones"
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              notificacionesActivadas ? "translate-x-6" : "translate-x-1"
            }`}
          />
        </button>
      </div>

      {/* Cambiar contraseña */}
      <div className="flex items-center justify-between bg-gray-100 rounded-lg p-4">
        <div>
          <p className="font-medium">Contraseña</p>
          <p className="text-xs text-gray-500">Cambia tu contraseña de acceso</p>
        </div>
        <button
          onClick={() => setShowChangePassword(true)}
          className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg transition"
        >
          Cambiar
        </button>
      </div>

      {showChangePassword && (
        <ChangePasswordModal onClose={() => setShowChangePassword(false)} />
      )}
    </div>
  );
}
